import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  type Firestore,
} from "firebase/firestore";
import { db } from "@/firebase/app";
import {
  parseCompetitionInstance,
  parseCompetitionTemplate,
  type LeagueCompetitionInstance,
  type LeagueCompetitionTemplate,
} from "@/models/competition";
import { eventDisplays } from "@/models/eventDisplay";
import {
  actionAuditScopes,
  canReadBroadcastOverlays,
  canUseAI,
} from "@/models/featureAccessPolicy";
import { parseMemberSummary, type LeagueMemberSummary } from "@/models/memberSummary";
import { parseOverlayConfig, type OverlayConfig } from "@/models/overlayConfig";
import { parseAthleteValue, type LeagueAthleteValue } from "@/models/schema";
import { workspaceStorageKey, type SBotChatWorkspaceScope } from "@/models/aiChat";
import {
  configuredHomeDashboardCards,
  isMembershipCurrentlyActive,
  needsOnboarding,
  resolvedLeagueID,
  type HomeDashboardCardID,
  type SBotMembership,
  type SBotUser,
} from "@/models/user";
import {
  APP_NAVIGATION_DESTINATION_IDS,
  appDestinationDescriptor,
  appDestinationScope,
  appRouteToSPAPath,
  isEntityAppDestination,
  isResourceAppDestination,
  isWorkspaceScopedAppDestination,
  makeAppEntityHandle,
  validateAppRoute,
  type SBotAppDestinationDescriptor,
  type SBotAppDestinationID,
  type SBotAppNavigationResourceResolution,
  type SBotAppRoute,
} from "./appNavigation";

export interface SBotAppNavigationAuthorizationContext {
  currentUser: SBotUser | null;
  isSBotLabsAdmin: boolean;
  /** Immutable workspace captured by the chat; preferred over mutable activeContext. */
  workspaceScope?: SBotChatWorkspaceScope | undefined;
}

export interface SBotAuthorizedAppRoute {
  route: SBotAppRoute;
  appPath: string;
  resource?: SBotAppNavigationResourceResolution | undefined;
}

export type SBotAppNavigationAuthorizationErrorKind =
  | "notSignedIn"
  | "onboardingRequired"
  | "noActiveLeague"
  | "workspaceMismatch"
  | "permissionDenied"
  | "invalidRoute"
  | "resourceValidationUnavailable"
  | "resourceNotFound";

const AUTHORIZATION_ERROR_MESSAGES: Record<SBotAppNavigationAuthorizationErrorKind, string> = {
  notSignedIn: "Sign in before opening this SBot destination.",
  onboardingRequired: "Finish onboarding before opening this SBot destination.",
  noActiveLeague: "Select an active league workspace before opening this destination.",
  workspaceMismatch: "This link belongs to a different workspace. Switch workspaces before opening it.",
  permissionDenied: "This destination is not available to the current user.",
  invalidRoute: "This SBot destination is malformed.",
  resourceValidationUnavailable: "SBot could not validate this destination against its source data.",
  resourceNotFound: "This destination no longer exists or is no longer available.",
};

export class SBotAppNavigationAuthorizationError extends Error {
  readonly kind: SBotAppNavigationAuthorizationErrorKind;

  constructor(kind: SBotAppNavigationAuthorizationErrorKind) {
    super(AUTHORIZATION_ERROR_MESSAGES[kind]);
    this.name = "SBotAppNavigationAuthorizationError";
    this.kind = kind;
  }
}

export interface SBotAppNavigationResourceChecker {
  resolve(
    route: SBotAppRoute,
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotAppNavigationResourceResolution | null>;
}

export type SBotWorkspaceNavigationResourceKind = Extract<
  SBotAppDestinationID,
  "competition" | "athlete" | "staff" | "overlay"
>;

export interface SBotWorkspaceNavigationIndexResult {
  descriptors: SBotAppDestinationDescriptor[];
  unavailableResourceKinds: SBotWorkspaceNavigationResourceKind[];
}

export interface SBotWorkspaceNavigationIndex {
  resources(
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotWorkspaceNavigationIndexResult>;
}

export interface SBotWorkspaceNavigationDataSource {
  competitions(leagueID: string): Promise<{
    instances: LeagueCompetitionInstance[];
    templates: LeagueCompetitionTemplate[];
  }>;
  athletes(leagueID: string, organizationID?: string): Promise<LeagueAthleteValue[]>;
  staff(leagueID: string, organizationID?: string): Promise<LeagueMemberSummary[]>;
  overlays(leagueID: string): Promise<OverlayConfig[]>;
}

/**
 * Chat-scoped navigation stays bound to the workspace captured when the chat
 * was created. Non-chat callers fall back to the mutable active context.
 */
export function activeLeagueIDForAppNavigation(
  context: SBotAppNavigationAuthorizationContext,
): string | undefined {
  const workspaceScope = context.workspaceScope;
  if (workspaceScope) {
    const leagueID = workspaceScope.leagueID
      ?? (workspaceScope.targetType === "league" ? workspaceScope.targetID : undefined);
    return leagueID?.trim() || undefined;
  }
  const activeContext = context.currentUser?.activeContext;
  if (!activeContext) return undefined;
  const leagueID = activeContext.leagueID
    ?? (activeContext.targetType === "league" ? activeContext.targetID : undefined);
  return leagueID?.trim() || undefined;
}

export function activeOrganizationIDForAppNavigation(
  context: SBotAppNavigationAuthorizationContext,
): string | undefined {
  const scope = context.workspaceScope;
  if (scope) {
    if (scope.targetType !== "organization") return undefined;
    return (scope.organizationID ?? scope.targetID).trim() || undefined;
  }
  const activeContext = context.currentUser?.activeContext;
  if (activeContext?.targetType !== "organization") return undefined;
  return (activeContext.organizationID ?? activeContext.targetID).trim() || undefined;
}

export function workspaceStorageKeyForAppNavigation(
  context: SBotAppNavigationAuthorizationContext,
): string | undefined {
  if (context.workspaceScope) return context.workspaceScope.storageKey;
  const activeContext = context.currentUser?.activeContext;
  if (!activeContext) return undefined;
  return workspaceStorageKey(activeContext.targetType, activeContext.targetID, activeContext.role);
}

export function workspaceMembershipForAppNavigation(
  context: SBotAppNavigationAuthorizationContext,
): SBotMembership | undefined {
  const user = context.currentUser;
  if (!user) return undefined;
  const scope = context.workspaceScope ?? user.activeContext;
  if (!scope) return undefined;
  return user.memberships.find((membership) =>
    isMembershipCurrentlyActive(membership)
      && membership.targetType === scope.targetType
      && membership.targetID === scope.targetID
      && membership.role === scope.role);
}

/** Stable non-entity destinations authorized in the exact active workspace. */
export function authorizedAppNavigationCatalog(
  context: SBotAppNavigationAuthorizationContext,
): SBotAppDestinationDescriptor[] {
  const user = context.currentUser;
  if (!user || needsOnboarding(user)) return [];
  const activeLeagueID = activeLeagueIDForAppNavigation(context);

  return APP_NAVIGATION_DESTINATION_IDS.flatMap((destination) => {
    if (
      appDestinationScope(destination) === "entity"
      || appDestinationScope(destination) === "resource"
    ) return [];
    const route: SBotAppRoute = appDestinationScope(destination) === "workspace"
      ? { destination, leagueID: activeLeagueID }
      : { destination };
    try {
      return [appDestinationDescriptor(authorizeAppRoute(route, context))];
    } catch {
      return [];
    }
  });
}

/**
 * Revalidates current authentication, exact workspace identity, and the same
 * helpers used by the production route guards. Entity existence is handled by
 * SBotAppNavigationAuthorizer after this policy preflight.
 */
export function authorizeAppRoute(
  route: SBotAppRoute,
  context: SBotAppNavigationAuthorizationContext,
): SBotAppRoute {
  const user = context.currentUser;
  if (!user) throw new SBotAppNavigationAuthorizationError("notSignedIn");
  if (needsOnboarding(user)) {
    throw new SBotAppNavigationAuthorizationError("onboardingRequired");
  }

  let validated: SBotAppRoute;
  try {
    validated = validateAppRoute(route);
  } catch {
    throw new SBotAppNavigationAuthorizationError("invalidRoute");
  }

  if (isWorkspaceScopedAppDestination(validated.destination)) {
    const activeLeagueID = activeLeagueIDForAppNavigation(context);
    if (!activeLeagueID) {
      throw new SBotAppNavigationAuthorizationError("noActiveLeague");
    }
    if (validated.leagueID !== activeLeagueID) {
      throw new SBotAppNavigationAuthorizationError("workspaceMismatch");
    }
  }

  if (!canOpenDestination(validated.destination, user, context)) {
    throw new SBotAppNavigationAuthorizationError("permissionDenied");
  }
  return validated;
}

export class FirestoreSBotWorkspaceNavigationDataSource implements SBotWorkspaceNavigationDataSource {
  constructor(private readonly firestore: Firestore = db) {}

  async competitions(leagueID: string): Promise<{
    instances: LeagueCompetitionInstance[];
    templates: LeagueCompetitionTemplate[];
  }> {
    const [instanceSnapshot, templateSnapshot] = await Promise.all([
      getDocs(query(
        collection(this.firestore, "leagueCompetitionInstances"),
        where("leagueID", "==", leagueID),
      )),
      getDocs(query(
        collection(this.firestore, "leagueCompetitionTemplates"),
        where("leagueID", "==", leagueID),
      )),
    ]);
    return {
      instances: instanceSnapshot.docs.flatMap((snapshot) => {
        const value = parseCompetitionInstance(snapshot.id, snapshot.data());
        return value ? [value] : [];
      }),
      templates: templateSnapshot.docs.flatMap((snapshot) => {
        const value = parseCompetitionTemplate(snapshot.id, snapshot.data());
        return value ? [value] : [];
      }),
    };
  }

  async athletes(leagueID: string, organizationID?: string): Promise<LeagueAthleteValue[]> {
    const reference = collection(this.firestore, "leagueAthleteValues");
    const snapshot = await getDocs(organizationID
      ? query(
        reference,
        where("leagueID", "==", leagueID),
        where("status", "==", "active"),
        where("organizationID", "==", organizationID),
      )
      : query(
        reference,
        where("leagueID", "==", leagueID),
        where("status", "==", "active"),
      ));
    return snapshot.docs.flatMap((document) => {
      const value = parseAthleteValue(document.id, document.data());
      return value ? [value] : [];
    });
  }

  async staff(leagueID: string, organizationID?: string): Promise<LeagueMemberSummary[]> {
    const reference = collection(this.firestore, "leagueMemberSummaries");
    const snapshot = await getDocs(organizationID
      ? query(
        reference,
        where("leagueID", "==", leagueID),
        where("organizationIDs", "array-contains", organizationID),
      )
      : query(reference, where("leagueID", "==", leagueID)));
    return snapshot.docs.flatMap((document) => {
      const value = parseMemberSummary(document.id, document.data());
      return value ? [value] : [];
    });
  }

  async overlays(leagueID: string): Promise<OverlayConfig[]> {
    const snapshot = await getDocs(query(
      collection(this.firestore, "overlayConfigs"),
      where("leagueID", "==", leagueID),
    ));
    return snapshot.docs.flatMap((document) => {
      const value = parseOverlayConfig(document.id, document.data());
      return value ? [value] : [];
    });
  }
}

type ResourceLoad<Value> =
  | { status: "skipped" }
  | { status: "loaded"; value: Value }
  | { status: "failed" };

interface WorkspaceNavigationBuildResult extends SBotWorkspaceNavigationIndexResult {
  resolutions: Map<string, SBotAppNavigationResourceResolution>;
}

/**
 * Shared resource index for catalog discovery and live entity reauthorization.
 * Each resource family fails independently; overlay discovery additionally
 * fails closed when its GPS competition parents cannot be verified.
 */
export class SBotWorkspaceNavigationIndexService
implements SBotWorkspaceNavigationIndex, SBotAppNavigationResourceChecker {
  constructor(private readonly dataSource: SBotWorkspaceNavigationDataSource) {}

  async resources(
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotWorkspaceNavigationIndexResult> {
    const { descriptors, unavailableResourceKinds } = await this.build(context);
    return { descriptors, unavailableResourceKinds };
  }

  async resolve(
    route: SBotAppRoute,
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotAppNavigationResourceResolution | null> {
    if (!isEntityAppDestination(route.destination)) return null;
    const build = await this.build(context);
    return build.resolutions.get(routeKey(route)) ?? null;
  }

  private async build(
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<WorkspaceNavigationBuildResult> {
    const leagueID = activeLeagueIDForAppNavigation(context);
    if (!leagueID) {
      return { descriptors: [], unavailableResourceKinds: [], resolutions: new Map() };
    }

    const canIndexCompetition = canIndex("competition", leagueID, context);
    const canIndexAthlete = canIndex("athlete", leagueID, context);
    const canIndexStaff = canIndex("staff", leagueID, context);
    const canIndexOverlay = canIndex("overlay", leagueID, context);
    const needsCompetitions = canIndexCompetition || canIndexOverlay;
    const organizationID = activeOrganizationIDForAppNavigation(context);

    const [competitionLoad, athleteLoad, staffLoad, overlayLoad] = await Promise.all([
      loadResource(() => this.dataSource.competitions(leagueID), needsCompetitions),
      loadResource(() => this.dataSource.athletes(leagueID, organizationID), canIndexAthlete),
      loadResource(() => this.dataSource.staff(leagueID, organizationID), canIndexStaff),
      loadResource(() => this.dataSource.overlays(leagueID), canIndexOverlay),
    ]);

    const descriptors: SBotAppDestinationDescriptor[] = [];
    const unavailable = new Set<SBotWorkspaceNavigationResourceKind>();
    const resolutions = new Map<string, SBotAppNavigationResourceResolution>();
    const competitionDisplays = competitionLoad.status === "loaded"
      ? eventDisplays(
        competitionLoad.value.instances.filter((instance) =>
          instance.leagueID === leagueID && instance.status !== "archived"),
        competitionLoad.value.templates,
      )
      : [];

    if (competitionLoad.status === "failed") {
      if (canIndexCompetition) unavailable.add("competition");
      if (canIndexOverlay) unavailable.add("overlay");
    } else if (canIndexCompetition) {
      for (const display of competitionDisplays) {
        const route: SBotAppRoute = {
          destination: "competition",
          leagueID,
          resourceID: display.instance.id,
        };
        descriptors.push(entityDescriptor(
          route,
          display.eventName,
          `Home / Competitions / ${display.eventName}`,
        ));
        resolutions.set(routeKey(route), { competitionInstanceID: display.instance.id });
      }
    }

    if (athleteLoad.status === "failed") {
      unavailable.add("athlete");
    } else if (athleteLoad.status === "loaded") {
      for (const athlete of athleteLoad.value) {
        if (
          athlete.leagueID !== leagueID
          || athlete.status !== "active"
          || (organizationID !== undefined && athlete.organizationID !== organizationID)
        ) continue;
        const route: SBotAppRoute = { destination: "athlete", leagueID, resourceID: athlete.id };
        descriptors.push(entityDescriptor(route, athlete.name, `Home / Athletes / ${athlete.name}`));
        resolutions.set(routeKey(route), {});
      }
    }

    if (staffLoad.status === "failed") {
      unavailable.add("staff");
    } else if (staffLoad.status === "loaded") {
      for (const member of staffLoad.value) {
        if (
          member.leagueID !== leagueID
          || !member.roles.some(isStaffRole)
          || (organizationID !== undefined && !member.organizationIDs.includes(organizationID))
        ) continue;
        const route: SBotAppRoute = { destination: "staff", leagueID, resourceID: member.id };
        descriptors.push(entityDescriptor(
          route,
          member.displayName,
          `Home / League Staff / ${member.displayName}`,
        ));
        resolutions.set(routeKey(route), {});
      }
    }

    if (overlayLoad.status === "failed") {
      unavailable.add("overlay");
    } else if (
      overlayLoad.status === "loaded"
      && canIndexOverlay
      && competitionLoad.status === "loaded"
    ) {
      const gpsCompetitions = new Map(
        competitionDisplays
          .filter((display) => display.isGPSRace)
          .map((display) => [display.instance.id, display] as const),
      );
      for (const overlay of overlayLoad.value) {
        const competition = gpsCompetitions.get(overlay.competitionInstanceID);
        if (!competition || overlay.leagueID !== leagueID) continue;
        const route: SBotAppRoute = { destination: "overlay", leagueID, resourceID: overlay.id };
        descriptors.push(entityDescriptor(
          route,
          overlay.name,
          `Home / Broadcast Overlays / ${competition.eventName} / ${overlay.name}`,
        ));
        resolutions.set(routeKey(route), { competitionInstanceID: competition.instance.id });
      }
    }

    const authorizedDescriptors = descriptors.filter((descriptor) => {
      try {
        authorizeAppRoute(descriptor.route, context);
        return true;
      } catch {
        resolutions.delete(routeKey(descriptor.route));
        return false;
      }
    });
    return {
      descriptors: authorizedDescriptors,
      unavailableResourceKinds: RESOURCE_KIND_ORDER.filter((kind) => unavailable.has(kind)),
      resolutions,
    };
  }
}

/** Production Firestore implementation shared by chat tools and deep links. */
export class FirestoreSBotWorkspaceNavigationIndex extends SBotWorkspaceNavigationIndexService {
  private readonly firestore: Firestore;

  constructor(firestore: Firestore = db) {
    super(new FirestoreSBotWorkspaceNavigationDataSource(firestore));
    this.firestore = firestore;
  }

  override async resolve(
    route: SBotAppRoute,
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotAppNavigationResourceResolution | null> {
    if (route.destination !== "notificationDetail") return super.resolve(route, context);
    if (!route.resourceID) return null;
    const snapshot = await getDoc(doc(this.firestore, "userNotifications", route.resourceID));
    if (!snapshot.exists() || snapshot.data().userID !== context.currentUser?.id) return null;
    return {};
  }
}

/** Backward-compatible name for existing controller/deep-link construction. */
export class FirestoreSBotAppNavigationResourceChecker
  extends FirestoreSBotWorkspaceNavigationIndex {}

/**
 * Async boundary shared by tool calls and incoming deep links. Non-entity
 * routes need policy authorization only; entity routes always re-fetch.
 */
export class SBotAppNavigationAuthorizer {
  constructor(private readonly resourceChecker?: SBotAppNavigationResourceChecker) {}

  async authorize(
    route: SBotAppRoute,
    context: SBotAppNavigationAuthorizationContext,
  ): Promise<SBotAuthorizedAppRoute> {
    const authorizedRoute = authorizeAppRoute(route, context);
    if (
      !isEntityAppDestination(authorizedRoute.destination)
      && !isResourceAppDestination(authorizedRoute.destination)
    ) {
      return {
        route: authorizedRoute,
        appPath: appRouteToSPAPath(authorizedRoute),
      };
    }
    if (!this.resourceChecker) {
      throw new SBotAppNavigationAuthorizationError("resourceValidationUnavailable");
    }
    let resource: SBotAppNavigationResourceResolution | null;
    try {
      resource = await this.resourceChecker.resolve(authorizedRoute, context);
    } catch {
      throw new SBotAppNavigationAuthorizationError("resourceValidationUnavailable");
    }
    if (!resource) {
      throw new SBotAppNavigationAuthorizationError("resourceNotFound");
    }
    return {
      route: authorizedRoute,
      appPath: appRouteToSPAPath(authorizedRoute, resource),
      resource,
    };
  }
}

const RESOURCE_KIND_ORDER: SBotWorkspaceNavigationResourceKind[] = [
  "competition",
  "athlete",
  "staff",
  "overlay",
];

function canIndex(
  destination: SBotWorkspaceNavigationResourceKind,
  leagueID: string,
  context: SBotAppNavigationAuthorizationContext,
): boolean {
  try {
    authorizeAppRoute({ destination, leagueID, resourceID: "navigation-index-probe" }, context);
    return true;
  } catch {
    return false;
  }
}

async function loadResource<Value>(
  load: () => Promise<Value>,
  enabled: boolean,
): Promise<ResourceLoad<Value>> {
  if (!enabled) return { status: "skipped" };
  try {
    return { status: "loaded", value: await load() };
  } catch {
    return { status: "failed" };
  }
}

function entityDescriptor(
  route: SBotAppRoute,
  title: string,
  breadcrumb: string,
): SBotAppDestinationDescriptor {
  const destination = route.destination as SBotWorkspaceNavigationResourceKind;
  return {
    id: destination,
    title,
    breadcrumb,
    handle: makeAppEntityHandle(destination, route.resourceID!),
    route,
  };
}

function routeKey(route: SBotAppRoute): string {
  return `${route.destination}\u0000${route.leagueID ?? ""}\u0000${route.resourceID ?? ""}`;
}

function isStaffRole(role: LeagueMemberSummary["roles"][number]): boolean {
  return role === "admin"
    || role === "leagueManager"
    || role === "leagueStaff"
    || role === "organizationManager"
    || role === "organizationStaff";
}

function canOpenDestination(
  destination: SBotAppDestinationID,
  user: SBotUser,
  context: SBotAppNavigationAuthorizationContext,
): boolean {
  const { isSBotLabsAdmin } = context;
  const leagueID = activeLeagueIDForAppNavigation(context);

  switch (destination) {
    case "home":
    case "account":
    case "notifications":
    case "notificationDetail":
    case "requestSupport":
      return true;
    case "ai":
      return canUseAI(user, isSBotLabsAdmin, leagueID);
    case "admin":
    case "billing":
    case "leagueBilling":
    case "aiUsageLimits":
    case "supportInbox":
    case "espnAccessRequests":
      return isSBotLabsAdmin;
    case "league":
    case "athleteSchema":
    case "athleteCreation":
    case "competitionSchema":
    case "competitionCreation":
      return isSBotLabsAdmin || hasLeagueManagementMembership(user, leagueID);
    case "leagueSettings":
    case "leagueInvitations":
    case "organizationAdminSchema":
      return isSBotLabsAdmin || hasLeagueAdminMembership(user, leagueID);
    case "externalSources":
      return isSBotLabsAdmin
        || canShowActiveHomeCard("externalSources", context, leagueID)
        || hasLeagueManagementMembership(user, leagueID);
    case "recentActivity":
      return actionAuditScopes(user, isSBotLabsAdmin, leagueID).length > 0;
    case "competitions":
    case "competition":
      return canShowActiveHomeCard("leagueSchedule", context, leagueID);
    case "organizations":
      return canShowActiveHomeCard("organizations", context, leagueID);
    case "athletes":
    case "athlete":
      return canShowActiveHomeCard("athletes", context, leagueID);
    case "leagueStaff":
      return canShowActiveHomeCard("leagueStaff", context, leagueID);
    case "staff":
      return canShowActiveHomeCard("leagueStaff", context, leagueID)
        && (isSBotLabsAdmin || hasLeagueManagementMembership(user, leagueID));
    case "gpsMenu":
      return canShowActiveHomeCard("gpsTracking", context, leagueID);
    case "overlay":
      return !!leagueID
        && canShowActiveHomeCard("broadcastOverlays", context, leagueID)
        && canReadBroadcastOverlays(user, isSBotLabsAdmin, leagueID);
  }
}

function hasLeagueManagementMembership(
  user: SBotUser,
  leagueID: string | undefined,
): boolean {
  return !!leagueID && user.memberships.some((membership) =>
    isMembershipCurrentlyActive(membership)
      && resolvedLeagueID(membership) === leagueID
      && membership.targetType === "league"
      && (membership.role === "admin" || membership.role === "leagueManager"));
}

function hasLeagueAdminMembership(
  user: SBotUser,
  leagueID: string | undefined,
): boolean {
  return !!leagueID && user.memberships.some((membership) =>
    isMembershipCurrentlyActive(membership)
      && resolvedLeagueID(membership) === leagueID
      && membership.targetType === "league"
      && membership.role === "admin");
}

function membershipMatchesLeague(
  membership: SBotMembership | undefined,
  leagueID: string | undefined,
): boolean {
  return !!membership && !!leagueID && resolvedLeagueID(membership) === leagueID;
}

function canShowActiveHomeCard(
  cardID: HomeDashboardCardID,
  context: SBotAppNavigationAuthorizationContext,
  leagueID: string | undefined,
): boolean {
  if (context.isSBotLabsAdmin) return true;
  const membership = workspaceMembershipForAppNavigation(context);
  if (!membershipMatchesLeague(membership, leagueID)) return false;
  return configuredHomeDashboardCards(membership!).includes(cardID);
}
